import { Fragment, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { HMIField } from '../../types';

export interface ArrayGridFieldProps {
  field: HMIField;
  value: number[];
  disabled?: boolean;
  onCommit: (value: number[]) => void;
}

/** Highest value found across a row's cells; 0 when the row has no cells on. */
function rowMax(cells: number[], rowIndex: number, cols: number): number {
  let max = 0;
  for (let c = 0; c < cols; c += 1) {
    const v = cells[rowIndex * cols + c] ?? 0;
    if (v > max) max = v;
  }
  return max;
}

/**
 * Renders `datatype: "array"` controls as a grid, e.g. the BMT Penetration /
 * Distribution lamp-power matrix (8 rows x 10 columns = 80 cells).
 * `gridRows`/`gridCols` are authored on the HMI template; defaults to a
 * single row when absent.
 *
 * Each row has a "%" box holding that row's power level and a bank of
 * ON/OFF lamp cells. A cell is ON when its underlying value equals the
 * row's % value; clicking it toggles between the % value (ON) and 0
 * (OFF). Toggling is only allowed while the row's % is > 0 and <= 100 -
 * the % box itself is also editable, which re-levels any cells that were
 * already ON in that row to the newly typed value.
 */
function ArrayGridField({ field, value, disabled, onCommit }: ArrayGridFieldProps) {
  const rows = field.gridRows ?? 1;
  const cols = field.gridCols ?? (field.arrayLength ?? value.length ?? 1);
  const percentMin = field.rangeMin ?? 0;
  const percentMax = field.rangeMax ?? 100;
  const [draft, setDraft] = useState<number[]>(value);
  const [percents, setPercents] = useState<number[]>(() =>
    Array.from({ length: rows }, (_, r) => rowMax(value, r, cols))
  );

  useEffect(() => {
    setDraft(value);
    setPercents(Array.from({ length: rows }, (_, r) => rowMax(value, r, cols)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const isTogglable = (percent: number) => percent > 0 && percent <= percentMax;

  const handleCellClick = (r: number, c: number) => {
    if (disabled) return;
    const percent = percents[r];
    if (!isTogglable(percent)) return;
    const index = r * cols + c;
    const isOn = draft[index] === percent;
    const next = [...draft];
    next[index] = isOn ? 0 : percent;
    setDraft(next);
    onCommit(next);
  };

  const handlePercentBlur = (r: number, raw: string) => {
    const parsed = raw === '' ? 0 : Number(raw);
    if (Number.isNaN(parsed)) return;
    const clamped = Math.min(percentMax, Math.max(percentMin, parsed));
    const oldPercent = percents[r];
    if (clamped !== oldPercent) {
      const nextPercents = [...percents];
      nextPercents[r] = clamped;
      setPercents(nextPercents);
    }

    // Any cells already ON at the old % move up/down to the newly typed %.
    if (oldPercent > 0) {
      const next = [...draft];
      let changed = false;
      for (let c = 0; c < cols; c += 1) {
        const index = r * cols + c;
        if (next[index] === oldPercent) {
          next[index] = clamped;
          changed = true;
        }
      }
      if (changed) {
        setDraft(next);
        onCommit(next);
      }
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `24px 48px repeat(${cols}, 36px)`,
          gap: '2px',
        }}
      >
        <Box />
        <Box />
        {Array.from({ length: cols }, (_, c) => (
          <Typography key={`col-${c}`} variant="caption" align="center">
            {c + 1}
          </Typography>
        ))}
        {Array.from({ length: rows }, (_, r) => {
          const percent = percents[r];
          const togglable = isTogglable(percent);
          return (
            <Fragment key={`row-${r}`}>
              <Typography variant="caption" sx={{ alignSelf: 'center', textAlign: 'center' }}>
                {r + 1}
              </Typography>
              <input
                key={`percent-${r}-${percent}`}
                type="number"
                disabled={disabled}
                defaultValue={percent}
                min={percentMin}
                max={percentMax}
                style={{
                  width: 44,
                  height: 24,
                  textAlign: 'center',
                  border: '1px solid #999',
                  borderRadius: 2,
                  background: disabled ? '#f0f0f0' : '#fff',
                }}
                onBlur={(e) => handlePercentBlur(r, e.target.value)}
              />
              {Array.from({ length: cols }, (_, c) => {
                const index = r * cols + c;
                const isOn = togglable && draft[index] === percent;
                return (
                  <Box
                    key={`cell-${r}-${c}`}
                    role="button"
                    aria-pressed={isOn}
                    onClick={() => handleCellClick(r, c)}
                    sx={{
                      width: 32,
                      height: 24,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 10,
                      fontWeight: 600,
                      borderRadius: '4px',
                      border: '1px solid',
                      borderColor: isOn ? '#2e7d32' : '#bdbdbd',
                      background: isOn ? '#66bb6a' : '#e0e0e0',
                      color: isOn ? '#fff' : '#757575',
                      cursor: disabled || !togglable ? 'not-allowed' : 'pointer',
                      opacity: disabled ? 0.6 : 1,
                      userSelect: 'none',
                    }}
                  >
                    {isOn ? 'ON' : 'OFF'}
                  </Box>
                );
              })}
            </Fragment>
          );
        })}
      </Box>
    </Box>
  );
}

export default ArrayGridField;
